import React, { useState } from 'react';
import { ChevronLeft, Combine, DollarSign, HandPlatter } from 'lucide-react';
import { formatCurrency } from '../../utils/format-currency';

// Components from central components/POS
import { TableCard } from '../../components/POS/TableCard';
import { ProductPicker } from '../../components/POS/ProductPicker';
import { JoinTablesModal } from '../../components/POS/JoinTablesModal';
import { SplitOrderModal } from '../../components/POS/SplitOrderModal';

// Components from other modules
import { TakeawayTile } from '../../components/takeaway/TakeawayTile';
import { PaymentModal } from '../../components/CashRegister/PaymentModal';
import { OpenSessionModal } from '../../components/CashRegister/OpenSessionModal';
import FlavorSelectorModal from '../../components/Flavors/FlavorSelectorModal';
import AdditionsExceptionsModal from '../../components/Options/OptionsModal';
import { ConfirmCancelOrderModal } from '../../features/orders/components/ConfirmCancelOrderModal';
import { ReceiptRenderer } from '../../components/Receipt/ReceiptRenderer';

// Local UI components
import { CartPanel } from './components/CartPanel';
import { TakeawayBoard } from '../../features/pos/TakeawayBoard';
import { Button } from '../../components/ui';

import { PosProvider, usePos } from './PosContext';

interface POSPageProps {
  mode?: 'TABLE' | 'TAKEAWAY';
}

export function POSContent() {
  const {
    tables, products, categories, selectedTable, setSelectedTable,
    activeCategory, setActiveCategory, searchTerm, setSearchTerm,
    loadingTables, loadingProducts, isTakeawayMode,
    filteredProducts, hasActiveOrder, activeOrder, totals,
    mobileTab, setMobileTab, handleProductClick,
    activeOrderTotal, handleConfirmPayment, processingPayment,
    showOpenCash, setShowOpenCash, handleOpenSessionConfirm, openingCash,
    receiptToPrint, setReceiptToPrint,
    showPayment, setShowPayment,
    flavorContext, setFlavorContext, optionsKey, setOptionsKey,
    unjoinTableMutation, joinTablesMutation, 
    handleCancelActiveOrder, cancelOrderMutation,
    splitOrderMutation, cart, setCart, addProductToCart, addProductToActiveOrder,
  } = usePos();

  const [joinModalOpen, setJoinModalOpen] = useState(false);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [splitModalOpen, setSplitModalOpen] = useState(false);

  if (!isTakeawayMode && loadingTables) return (
    <div className="flex h-screen items-center justify-center">
      <p className="animate-pulse text-lg font-medium text-muted">Cargando mesas...</p>
    </div>
  );
  
  if (!isTakeawayMode && !selectedTable) {
    return (
      <div className="min-h-[100dvh] bg-[color:var(--surface-muted)] px-4 pb-24 pt-6 sm:px-6">
        <div className="mx-auto flex w-full max-w-5xl flex-col gap-6">
          <header className="flex items-center justify-between gap-4">
            <div className="space-y-1">
              <h1 className="text-2xl font-bold tracking-tight">POS - Mesas</h1>
              <p className="text-sm text-muted">Selecciona una mesa para gestionar pedidos.</p>
            </div>
            <div className="flex items-center gap-2">
              <Button onClick={() => setJoinModalOpen(true)} variant="outline" className="gap-2 rounded-full">
                <Combine size={18} /> Unir Mesas
              </Button>
            </div>
          </header>
          
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {tables?.map(t => (
              <TableCard key={t.id} table={t} onSelect={setSelectedTable} />
            ))}
          </div>

          <div className="mt-8 mb-4 flex items-center justify-between">
            <h2 className="text-xl font-bold tracking-tight">Pedidos Para Llevar (Mostrador)</h2>
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 pb-8">
            <TakeawayBoard />
          </div>
        </div>
        <JoinTablesModal 
          open={joinModalOpen} 
          tables={tables || []} 
          onClose={() => setJoinModalOpen(false)} 
          onConfirm={(p, s) => joinTablesMutation.mutate({ primaryId: p, secondaryId: s })}
          loading={joinTablesMutation.isPending}
        />
        <ReceiptRenderer data={receiptToPrint} onComplete={() => setReceiptToPrint(null)} />
      </div>
    );
  }

  return (
    <div className="flex min-h-[100dvh] flex-col bg-[color:var(--color-bg)] pb-20 lg:pb-0">
      {/* Header */}
      <div className="flex items-center gap-4 px-4 pt-4 sm:px-6">
        <button 
          onClick={() => isTakeawayMode ? (window.location.hash = '#/pos') : setSelectedTable(null)} 
          className="rounded-full p-2 hover:bg-surface-muted transition"
        >
          <ChevronLeft size={24} />
        </button>
        <h1 className="text-xl font-bold">
          {isTakeawayMode ? 'Para Llevar' : `Mesa #${selectedTable?.number}`}
        </h1>
      </div>

      <div className="flex min-h-0 flex-1 flex-col gap-4 p-4 lg:flex-row lg:overflow-hidden">
        {/* Menu Section */}
        <main className={`min-h-0 flex-1 ${mobileTab === 'ORDER' ? 'hidden lg:block' : 'block'}`}>
          <ProductPicker
            products={filteredProducts}
            categories={categories || []}
            activeCategory={activeCategory}
            onCategorySelect={setActiveCategory}
            searchTerm={searchTerm}
            onSearchTermChange={setSearchTerm}
            onProductClick={handleProductClick}
          />
        </main>

        {/* Side Panel Section */}
        <aside className={`min-h-0 w-full lg:w-[400px] xl:w-[450px] ${mobileTab === 'MENU' ? 'hidden lg:block' : 'block'}`}>
          <div className="h-full overflow-y-auto">
            <CartPanel />
          </div>
        </aside>
      </div>

      {/* Modals & Overlays */}
      <PaymentModal 
        visible={showPayment} 
        total={activeOrderTotal} 
        onClose={() => setShowPayment(false)} 
        onConfirm={handleConfirmPayment} 
        loading={processingPayment} 
      />
      <OpenSessionModal 
        visible={showOpenCash} 
        onClose={() => setShowOpenCash(false)} 
        onConfirm={handleOpenSessionConfirm} 
        loading={openingCash} 
      />
      <ReceiptRenderer data={receiptToPrint} onComplete={() => setReceiptToPrint(null)} />
      
      {flavorContext && (
        <FlavorSelectorModal
          open={!!flavorContext}
          product={flavorContext.product}
          quantity={1}
          onClose={() => setFlavorContext(null)}
          onConfirm={(payload) => {
            if (hasActiveOrder) {
              addProductToActiveOrder(flavorContext.product, payload);
            } else {
              addProductToCart(flavorContext.product, payload.flavors, payload);
            }
            setFlavorContext(null);
          }}
        />
      )}

      {optionsKey && (
        <AdditionsExceptionsModal
          open={!!optionsKey}
          product={cart[optionsKey]?.product}
          onClose={() => setOptionsKey(null)}
          onApply={(updatedData) => {
            setCart(prev => ({
              ...prev,
              [optionsKey]: { ...prev[optionsKey], ...updatedData }
            }));
            setOptionsKey(null);
          }}
        />
      )}

      {splitModalOpen && (
        <SplitOrderModal
          open={splitModalOpen}
          order={activeOrder}
          onClose={() => setSplitModalOpen(false)}
          loading={splitOrderMutation.isPending}
          onConfirm={(itemIds) => {
            splitOrderMutation.mutate({ orderId: activeOrder!.id, itemIds });
            setSplitModalOpen(false);
          }}
        />
      )}

      {cancelModalOpen && (
        <ConfirmCancelOrderModal
          open={cancelModalOpen}
          onClose={() => setCancelModalOpen(false)}
          onConfirm={() => {
            handleCancelActiveOrder();
            setCancelModalOpen(false);
          }}
          order={activeOrder}
          loading={cancelOrderMutation.isPending}
        />
      )}

      {/* Mobile Navigation */}
      <div className="fixed bottom-0 left-0 right-0 z-50 h-16 border-t border-divider bg-surface flex items-center justify-around shadow-lg lg:hidden">
        <button 
          onClick={() => setMobileTab('MENU')} 
          className={`flex-1 flex flex-col items-center justify-center gap-1 transition ${mobileTab === 'MENU' ? 'text-primary' : 'text-muted'}`}
        >
          <HandPlatter size={22} />
          <span className="text-[10px] font-bold uppercase">Menú</span>
        </button>
        <div className="h-8 w-px bg-divider" />
        <button 
          onClick={() => setMobileTab('ORDER')} 
          className={`flex-1 flex flex-col items-center justify-center gap-1 transition ${mobileTab === 'ORDER' ? 'text-primary' : 'text-muted'}`}
        >
          <DollarSign size={22} />
          <span className="text-[10px] font-bold uppercase">{totals?.total ? formatCurrency(totals.total) : 'Orden'}</span>
        </button>
      </div>
    </div>
  );
}

export default function POSPage({ mode = 'TABLE' }: POSPageProps) {
  return (
    <PosProvider mode={mode}>
      <POSContent />
    </PosProvider>
  );
}
